import { Button as CollectrButton } from "./ui/button";
import { useEffect } from "react";
import { useRouteError } from "react-router-dom";
import * as Sentry from "@sentry/react";

import { isStaleDeploymentError } from "@/lib/staleDeployment";

/**
 * Foutpagina op routeniveau.
 *
 * WAAROM DIT BESTAAT. De applicatie draait op een DATA ROUTER
 * (`createBrowserRouter`). Die vangt fouten uit een route ZELF af en rendert
 * daarvoor zijn eigen standaardpagina — de React-error-boundaries eromheen
 * (`AppErrorBoundary`, `Sentry.ErrorBoundary`) komen er dus nooit aan te pas.
 * Het gevolg was zichtbaar op productie: een klant kreeg de
 * ontwikkelaarsmelding van React Router te zien, compleet met
 * "Hey developer 👋 — you can provide a way better UX than this".
 *
 * DE FOUT DIE DIT IN DE PRAKTIJK VEROORZAAKT is bijna altijd dezelfde: een
 * BROWSER MET EEN OUDE PAGINA NA EEN DEPLOY. De pagina die al openstond
 * verwijst naar chunknamen met een hash die na de nieuwe build niet meer
 * bestaat. Cloudflare Pages beantwoordt zo'n gemist bestand niet met 404 maar
 * met index.html (HTTP 200, text/html) vanwege de SPA-fallback, en de browser
 * weigert HTML als module te laden: "Failed to fetch dynamically imported
 * module". Dat is geen programmeerfout maar een verouderde pagina, en het
 * juiste antwoord is dus HERLADEN — niet een stacktrace.
 */

export function RouteErrorPage() {
  const error = useRouteError();
  /*
   * De opslag meegeven is wat de GEVOLGFOUT herkenbaar maakt: `React.lazy` leest
   * `.default` van een module die er niet is, en de TypeError die de gebruiker
   * dan bereikt zegt niets over chunks. Alleen wanneer er in deze sessie ook
   * werkelijk een preload is mislukt, telt die vorm als verouderde pagina.
   */
  const verouderd = isStaleDeploymentError(
    error,
    typeof window === "undefined" ? null : window.sessionStorage,
  );

  useEffect(() => {
    /*
     * Een verouderde pagina is geen incident: hij hoort bij elke deploy en zou
     * de foutentelling vervuilen. Alleen de rest gaat naar Sentry.
     */
    if (!verouderd) Sentry.captureException(error);
  }, [error, verouderd]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-neutral-50 px-6 text-center">
      <h1 className="text-lg font-semibold text-neutral-900">
        {verouderd ? "Er staat een nieuwe versie klaar" : "Er is iets misgegaan"}
      </h1>
      <p className="max-w-md text-sm text-neutral-600">
        {verouderd
          ? "Deze pagina stond nog open toen Collectr werd bijgewerkt. Herlaad om verder te gaan; je gegevens zijn niet geraakt."
          : "We konden dit scherm niet laden. Probeer te herladen. Blijft het misgaan, meld het dan met het tijdstip erbij."}
      </p>
      <CollectrButton
        type="button"
        onClick={() => window.location.reload()}

      >
        Pagina herladen
      </CollectrButton>
    </div>
  );
}
